import { COUNTRY_CODES, DEPOT_GET, DEPOT_UPDATE } from '@constants';
import { DepotFormValues } from '@forms/DepotForm/types';
import { DepotForm } from '@forms/DepotForm/DepotForm';
import { STANDARD_BUSINESS_HOURS } from '@forms/DepotForm/constants';
import { useAuthHttp } from '@hooks';
import { Depot } from '@views/DepotDetails/DepotDetails';
import { AxiosResponse } from 'axios';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDocumentTitle } from 'usehooks-ts';
import { FloxSpinner } from '@tailwindComponents/FloxSpinner';

export function UpdateDepot() {
  useDocumentTitle('FLOX - Update Depot');
  const authHttp = useAuthHttp();
  const navigate = useNavigate();
  const authHttpFormData = useAuthHttp('multipart/form-data');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetchingDepot, setIsFetchingDepot] = useState(false);
  const [depot, setDepot] = useState<Depot>();
  const { id } = useParams<{ id: string }>();

  const pages = [
    { name: 'Home', href: '/', current: false },
    { name: 'Depots', href: '/depot-master', current: false },
    {
      name: 'Update Depot',
      href: `/depot/update/${id}`,
      current: true,
    },
  ];

  const fetchDepot = async () => {
    setIsFetchingDepot(true);
    try {
      const { data }: AxiosResponse<{ depot: Depot }> = await authHttp.get(
        DEPOT_GET,
        {
          params: {
            depotId: id,
          },
        }
      );
      setDepot(data.depot);
    } catch (error) {
      console.error(error);
    } finally {
      setIsFetchingDepot(false);
    }
  };

  const handleOnSubmit = async (data: DepotFormValues) => {
    setIsSubmitting(true);
    try {
      const { data: depotData } = await authHttpFormData.post(
        DEPOT_UPDATE,
        {
          depot: {
            depotId: depot?.depotId,
            name: data.name,
            description: data.description,
            address: data.address,
            postcode: data.postcode,
            city: data.city,
            country: data.country,
            location: data.location,
            contactName: data.contactName,
            contactEmail: data.contactEmail,
            phoneNumber: {
              countryCode: COUNTRY_CODES.find(
                (country) => country.country_code === data.phoneCountryCode
              )?.country_code,
              countryNumberPrefix: COUNTRY_CODES.find(
                (country) => country.country_code === data.phoneCountryCode
              )?.phone_code.toString(),
              number: data.phoneNumber,
            },
            loadingDocks: data.loadingDocks ? Number(data.loadingDocks) : 0,
            standardBusinessHoursType: data.schedule.value,
            hasFlexibleBusinessHours: data.flexibleHoursOnDemand,
            customBusinessHours:
              data.schedule.value === 'Other'
                ? [
                    {
                      day: 'Monday',
                      open: !data.mondayWorkingHours.working
                        ? '00:00:00'
                        : data.mondayWorkingHours.openingTime,
                      close: !data.mondayWorkingHours.working
                        ? '00:00:00'
                        : data.mondayWorkingHours.closingTime,
                    },
                    {
                      day: 'Tuesday',
                      open: !data.tuesdayWorkingHours.working
                        ? '00:00:00'
                        : data.tuesdayWorkingHours.openingTime,
                      close: !data.tuesdayWorkingHours.working
                        ? '00:00:00'
                        : data.tuesdayWorkingHours.closingTime,
                    },
                    {
                      day: 'Wednesday',
                      open: !data.wednesdayWorkingHours.working
                        ? '00:00:00'
                        : data.wednesdayWorkingHours.openingTime,
                      close: !data.wednesdayWorkingHours.working
                        ? '00:00:00'
                        : data.wednesdayWorkingHours.closingTime,
                    },
                    {
                      day: 'Thursday',
                      open: !data.thursdayWorkingHours.working
                        ? '00:00:00'
                        : data.thursdayWorkingHours.openingTime,
                      close: !data.thursdayWorkingHours.working
                        ? '00:00:00'
                        : data.thursdayWorkingHours.closingTime,
                    },
                    {
                      day: 'Friday',
                      open: !data.fridayWorkingHours.working
                        ? '00:00:00'
                        : data.fridayWorkingHours.openingTime,
                      close: !data.fridayWorkingHours.working
                        ? '00:00:00'
                        : data.fridayWorkingHours.closingTime,
                    },
                    {
                      day: 'Saturday',
                      open: !data.saturdayWorkingHours.working
                        ? '00:00:00'
                        : data.saturdayWorkingHours.openingTime,
                      close: !data.saturdayWorkingHours.working
                        ? '00:00:00'
                        : data.saturdayWorkingHours.closingTime,
                    },
                    {
                      day: 'Sunday',
                      open: !data.sundayWorkingHours.working
                        ? '00:00:00'
                        : data.sundayWorkingHours.openingTime,
                      close: !data.sundayWorkingHours.working
                        ? '00:00:00'
                        : data.sundayWorkingHours.closingTime,
                    },
                  ]
                : [],
            vehicleLoadingTimeWindows: [
              {
                day: 'Monday',
                open: !data.mondayLoadingHours.operating
                  ? '00:00:00'
                  : data.mondayLoadingHours.openingTime,
                close: !data.mondayLoadingHours.operating
                  ? '00:00:00'
                  : data.mondayLoadingHours.closingTime,
              },
              {
                day: 'Tuesday',
                open: !data.tuesdayLoadingHours.operating
                  ? '00:00:00'
                  : data.tuesdayLoadingHours.openingTime,
                close: !data.tuesdayLoadingHours.operating
                  ? '00:00:00'
                  : data.tuesdayLoadingHours.closingTime,
              },
              {
                day: 'Wednesday',
                open: !data.wednesdayLoadingHours.operating
                  ? '00:00:00'
                  : data.wednesdayLoadingHours.openingTime,
                close: !data.wednesdayLoadingHours.operating
                  ? '00:00:00'
                  : data.wednesdayLoadingHours.closingTime,
              },
              {
                day: 'Thursday',
                open: !data.thursdayLoadingHours.operating
                  ? '00:00:00'
                  : data.thursdayLoadingHours.openingTime,
                close: !data.thursdayLoadingHours.operating
                  ? '00:00:00'
                  : data.thursdayLoadingHours.closingTime,
              },
              {
                day: 'Friday',
                open: !data.fridayLoadingHours.operating
                  ? '00:00:00'
                  : data.fridayLoadingHours.openingTime,
                close: !data.fridayLoadingHours.operating
                  ? '00:00:00'
                  : data.fridayLoadingHours.closingTime,
              },
              {
                day: 'Saturday',
                open: !data.saturdayLoadingHours.operating
                  ? '00:00:00'
                  : data.saturdayLoadingHours.openingTime,
                close: !data.saturdayLoadingHours.operating
                  ? '00:00:00'
                  : data.saturdayLoadingHours.closingTime,
              },
              {
                day: 'Sunday',
                open: !data.sundayLoadingHours.operating
                  ? '00:00:00'
                  : data.sundayLoadingHours.openingTime,
                close: !data.sundayLoadingHours.operating
                  ? '00:00:00'
                  : data.sundayLoadingHours.closingTime,
              },
            ],
            fleet: [
              {
                type: 'MultiAxleArticMoreThan26t',
                count: data.articsOver26t,
              },
              {
                type: 'ThreeAxleRigidLessThan26t',
                count: data.rigidsUnder26t,
              },
              { type: 'TwoAxleRigid18t', count: data.rigidsUnder18t },
              {
                type: 'TwoAxleRigidLessThan7AndHalf5t',
                count: data.rigidsUnder7_5t,
              },
              {
                type: 'LgvLessThan3AndHalft',
                count: data.ldvsUnder3_5t,
              },
              {
                type: 'VanLessThan1AndHalft',
                count: data.vansUnder1_5t,
              },
              { type: 'SpecialOrOther', count: data.specialOther },
            ],
          },
          images: data.depotImages.map((image) => ({
            id: crypto.randomUUID(),
            title: image.name,
            image,
          })),
          deletedImageIds: data.deletedImagesIds,
        },
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (depotData) {
        navigate(`/depot/${depotData.depot.depotId}`);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    fetchDepot();
  }, []);

  if (isFetchingDepot) {
    return (
      <div className='flex flex-row justify-center items-center mt-12'>
        <FloxSpinner size='lg' />
      </div>
    );
  }

  if (!depot) {
    return (
      <div className='flex flex-row justify-center items-center mt-12'>
        <span className='text-gray-700 text-lg'>No depot found</span>
      </div>
    );
  }

  const getWorkingHoursForDay = (
    day: string,
    customBusinessHours: Array<{ day: string; open?: string; close?: string }>
  ) => {
    const hours = customBusinessHours?.find((d) => d.day === day);
    return {
      working: Boolean(
        hours?.open !== '00:00:00' && hours?.close !== '00:00:00'
      ),
      openingTime: hours?.open?.slice(0, 5) || '06:00',
      closingTime: hours?.close?.slice(0, 5) || '18:00',
    };
  };

  const getLoadingHoursForDay = (
    day: string,
    vehicleLoadingTimeWindows: Array<{
      day: string;
      open?: string;
      close?: string;
    }>
  ) => {
    const hours = vehicleLoadingTimeWindows?.find((d) => d.day === day);
    return {
      operating: Boolean(
        hours?.open !== '00:00:00' && hours?.close !== '00:00:00'
      ),
      openingTime: hours?.open?.slice(0, 5) || '06:00',
      closingTime: hours?.close?.slice(0, 5) || '18:00',
    };
  };

  const getFleetCountString = (
    fleet: Array<{ type: string; count: number }>,
    type: string
  ): string => {
    const vehicle = fleet?.find((v) => v.type === type);
    return vehicle &&
      vehicle.count !== undefined &&
      vehicle.count !== null &&
      vehicle.count > 0
      ? vehicle.count.toString()
      : '';
  };

  const processDepotToInitialValues = (): DepotFormValues => {
    return {
      name: depot.name || '',
      description: depot.description || '',
      address: depot.address || '',
      postcode: depot.postcode || '',
      city: depot.city || '',
      country: depot.country || '',
      location: depot.location || { lat: 0, lon: 0 },
      images: depot.images || [],
      depotImages: [],
      deletedImagesIds: [],
      contactName: depot.contactName || '',
      contactEmail: depot.contactEmail || '',
      phoneCountryCode:
        COUNTRY_CODES.find(
          (country) =>
            country.country_code === depot.phoneNumber?.countryCode
        )?.country_code || 'GB',
      phoneNumber: depot.phoneNumber?.number || '',
      loadingDocks: depot.loadingDocks?.toString() || '',
      haveOwnFleet:
        depot.fleet?.some((vehicle) => Number(vehicle.count) > 0) || false,
      articsOver26t: getFleetCountString(
        depot.fleet || [],
        'MultiAxleArticMoreThan26t'
      ),
      rigidsUnder26t: getFleetCountString(
        depot.fleet || [],
        'ThreeAxleRigidLessThan26t'
      ),
      rigidsUnder18t: getFleetCountString(
        depot.fleet || [],
        'TwoAxleRigid18t'
      ),
      rigidsUnder7_5t: getFleetCountString(
        depot.fleet || [],
        'TwoAxleRigidLessThan7AndHalf5t'
      ),
      ldvsUnder3_5t: getFleetCountString(
        depot.fleet || [],
        'LgvLessThan3AndHalft'
      ),
      vansUnder1_5t: getFleetCountString(
        depot.fleet || [],
        'VanLessThan1AndHalft'
      ),
      specialOther: getFleetCountString(depot.fleet || [], 'SpecialOrOther'),
      schedule: STANDARD_BUSINESS_HOURS.find(
        (option) => option.value === depot.standardBusinessHoursType
      ) || STANDARD_BUSINESS_HOURS[0],
      flexibleHoursOnDemand: depot.hasFlexibleBusinessHours || false,
      mondayWorkingHours: getWorkingHoursForDay(
        'Monday',
        depot.customBusinessHours || []
      ),
      tuesdayWorkingHours: getWorkingHoursForDay(
        'Tuesday',
        depot.customBusinessHours || []
      ),
      wednesdayWorkingHours: getWorkingHoursForDay(
        'Wednesday',
        depot.customBusinessHours || []
      ),
      thursdayWorkingHours: getWorkingHoursForDay(
        'Thursday',
        depot.customBusinessHours || []
      ),
      fridayWorkingHours: getWorkingHoursForDay(
        'Friday',
        depot.customBusinessHours || []
      ),
      saturdayWorkingHours: getWorkingHoursForDay(
        'Saturday',
        depot.customBusinessHours || []
      ),
      sundayWorkingHours: getWorkingHoursForDay(
        'Sunday',
        depot.customBusinessHours || []
      ),
      mondayLoadingHours: getLoadingHoursForDay(
        'Monday',
        depot.vehicleLoadingTimeWindows || []
      ),
      tuesdayLoadingHours: getLoadingHoursForDay(
        'Tuesday',
        depot.vehicleLoadingTimeWindows || []
      ),
      wednesdayLoadingHours: getLoadingHoursForDay(
        'Wednesday',
        depot.vehicleLoadingTimeWindows || []
      ),
      thursdayLoadingHours: getLoadingHoursForDay(
        'Thursday',
        depot.vehicleLoadingTimeWindows || []
      ),
      fridayLoadingHours: getLoadingHoursForDay(
        'Friday',
        depot.vehicleLoadingTimeWindows || []
      ),
      saturdayLoadingHours: getLoadingHoursForDay(
        'Saturday',
        depot.vehicleLoadingTimeWindows || []
      ),
      sundayLoadingHours: getLoadingHoursForDay(
        'Sunday',
        depot.vehicleLoadingTimeWindows || []
      ),
    };
  };

  return (
    <DepotForm
      onSubmit={handleOnSubmit}
      isSubmitting={isSubmitting}
      header='Update Depot'
      pages={pages}
      initialValues={processDepotToInitialValues()}
    />
  );
}
